import type { ServicePageData } from '../types.ts';

export const SERVICES: ServicePageData[] = [
  {
    slug: 'sap',
    tag: 'SAP',
    pageTitle: 'SAP Consulting Services — Softfinity Consulting',
    title: 'SAP Consulting Services',
    lead: 'Overhaul and optimise enterprise operations with expert SAP consulting. Our specialists help organisations simplify business processes, improve operational efficiency, and maximise return on investment across the full SAP stack.',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Areas of expertise',
    areas: [
      'SAP S/4HANA', 'SAP ECC', 'SAP Finance', 'SAP Supply Chain',
      'SAP SuccessFactors', 'SAP Analytics', 'SAP Integration',
      'SAP Migration & Upgrades', 'SAP Cloud Solutions',
    ],
    areasLead: 'We help organisations modernise ERP environments without disrupting the operations that run on them.',
    outcomes: [
      { num: '01', title: 'ERP that fits', body: 'Modernise core systems without forcing your business to twist around the platform.' },
      { num: '02', title: 'Lower TCO', body: 'Right-size licensing, modules, and infrastructure. Pay for what you actually use.' },
      { num: '03', title: 'Continuity first', body: 'Migration and upgrade paths that protect operational continuity end-to-end.' },
    ],
    approachLead: 'Every SAP engagement follows the Softfinity consulting approach. Our consultants embed with your team and stay through the full journey.',
  },
  {
    slug: 'microsoft',
    tag: 'Microsoft',
    pageTitle: 'Microsoft Consulting Services — Softfinity Consulting',
    title: 'Microsoft Consulting Services',
    lead: 'Get more from the Microsoft platform: productivity, collaboration, automation, and real operational change. Our consultants help organisations design, implement, and optimise Microsoft-based solutions.',
    heroImage: 'https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Areas of expertise',
    areas: [
      'Microsoft Azure', 'Microsoft 365', 'Dynamics 365', 'Power Platform',
      'Power BI', 'SharePoint', 'Teams', 'Enterprise Security',
      'Application Modernisation',
    ],
    areasLead: 'We help businesses get more from their Microsoft investment with solutions people actually adopt.',
    outcomes: [
      { num: '01', title: 'Productivity, raised', body: 'Modern collaboration that your people actually adopt — not another tool they ignore.' },
      { num: '02', title: 'Cloud done right', body: 'Azure architectures that scale with you, with cost control built in from day one.' },
      { num: '03', title: 'Automation that earns', body: 'Power Platform workflows tied to measurable business outcomes — no shelfware.' },
    ],
    approachLead: 'Every Microsoft engagement follows the Softfinity consulting approach. Our consultants embed with your team and stay through the full journey.',
  },
  {
    slug: 'oracle',
    tag: 'Oracle',
    pageTitle: 'Oracle Consulting Services — Softfinity Consulting',
    title: 'Oracle Consulting Services',
    lead: 'Get Oracle running at full capability. Our specialists help organisations implement and optimise Oracle technologies to improve financial management, cut operational drag, and put better data in front of the people who need it.',
    heroImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Areas of expertise',
    areas: [
      'Oracle Cloud Infrastructure', 'Oracle ERP', 'Oracle HCM', 'Oracle Database',
      'Oracle Analytics', 'Oracle Integration', 'Oracle Performance Optimisation',
      'Oracle Migrations',
    ],
    areasLead: 'We help organisations get more from Oracle without committing to a roadmap that serves the vendor more than the business.',
    outcomes: [
      { num: '01', title: 'Finance, modernised', body: 'Oracle ERP and HCM deployments aligned with how your finance and HR teams actually work.' },
      { num: '02', title: 'Performance you can feel', body: 'Database and infrastructure tuning that turns slow systems into competitive advantage.' },
      { num: '03', title: 'Smooth migrations', body: 'Lift-and-shift or re-platform — with continuity, governance, and roll-back planned in.' },
    ],
    approachLead: 'Every Oracle engagement follows the Softfinity consulting approach. Our consultants embed with your team and stay through the full journey.',
  },
  {
    slug: 'ibm',
    tag: 'IBM',
    pageTitle: 'IBM Consulting Services — Softfinity Consulting',
    title: 'IBM Consulting Services',
    lead: 'Put enterprise-grade IBM technologies to work: building resilience, reducing complexity, and modernising how your organisation operates. Our consultants bring deep experience across IBM\'s product range.',
    heroImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Areas of expertise',
    areas: [
      'IBM Cloud', 'IBM Integration Solutions', 'IBM Automation', 'IBM Data Platforms',
      'IBM Security Solutions', 'IBM Middleware', 'Enterprise Integration',
    ],
    areasLead: 'We help clients modernise legacy environments and build platforms that hold up over time.',
    outcomes: [
      { num: '01', title: 'Integrate without rip-and-replace', body: 'Make heterogeneous estates speak the same language — without an 18-month re-platform.' },
      { num: '02', title: 'Resilient by design', body: 'Architectures and security postures that absorb shocks instead of cascading them.' },
      { num: '03', title: 'Modernise legacy, safely', body: 'Stage modernisation so risk drops at every step — not all at the end.' },
    ],
    approachLead: 'Every IBM engagement follows the Softfinity consulting approach. Our consultants embed with your team and stay through the full journey.',
  },
  {
    slug: 'ai',
    tag: 'AI',
    pageTitle: 'Artificial Intelligence — Softfinity Consulting',
    title: 'Artificial Intelligence',
    lead: 'Put your data to work and turn the output into business value. AI is rapidly changing how organisations operate, make decisions, and engage with customers. Our consultants help businesses identify, design, and implement AI solutions that deliver measurable results.',
    heroImage: '/assets/ai-humans.jpg',
    areasEyebrow: 'AI services',
    areas: [
      'AI Strategy Development', 'Machine Learning Solutions', 'Predictive Analytics',
      'Generative AI', 'Intelligent Automation', 'Natural Language Processing',
      'AI Governance', 'AI Integration',
    ],
    areasLead: 'We help organisations adopt AI responsibly and turn it into measurable business results.',
    outcomes: [
      { num: '01', title: 'Hype, separated from substance', body: "An honest read on what AI can do for you today — and what it can't, yet." },
      { num: '02', title: 'Production over pilots', body: 'AI that ships into real workflows with monitoring, governance, and ROI tracked.' },
      { num: '03', title: 'Adoption, not just deployment', body: "We bring your people with the work so the model doesn't become a science project." },
    ],
    approachLead: 'Every AI engagement follows the Softfinity consulting approach. Our consultants work directly with your team and stay through the full journey.',
  },
  {
    slug: 'cloud',
    tag: 'Cloud',
    pageTitle: 'Cloud Computing — Softfinity Consulting',
    title: 'Cloud Computing',
    lead: 'Move faster with secure, scalable, cost-effective cloud solutions. Cloud technologies help organisations improve agility, cut costs, and respond quickly to changing business requirements.',
    heroImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Cloud services',
    areas: [
      'Cloud Strategy & Assessment', 'Cloud Migration', 'Hybrid Cloud Solutions',
      'Multi-Cloud Architecture', 'Cloud Security', 'Cloud Governance',
      'Infrastructure Modernisation', 'Cloud Operations',
    ],
    areasLead: 'Our cloud experts help organisations build reliable platforms built for growth.',
    outcomes: [
      { num: '01', title: 'Right-sized, not oversold', body: 'Stop paying for unused modules and over-provisioned instances. We map every dollar.' },
      { num: '02', title: 'Portable by design', body: 'Architectures that can leave any one provider — your insurance policy against lock-in.' },
      { num: '03', title: 'Operationally sound', body: 'FinOps, observability, and security baked in from week one — not bolted on later.' },
    ],
    approachLead: 'Every Cloud engagement follows the Softfinity consulting approach. Our consultants work directly with your team and stay through the full journey.',
  },
  {
    slug: 'data',
    tag: 'Data',
    pageTitle: 'Big Data & Analytics — Softfinity Consulting',
    title: 'Big Data & Analytics',
    lead: 'Turn data into decisions that move the business. Organisations generate vast amounts of data every day. Our consultants help clients get value from it through better analytics, data platforms built for the actual workload, and reporting that drives decisions rather than debate.',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Big data services',
    areas: [
      'Data Strategy', 'Data Architecture', 'Data Warehousing', 'Data Lakes',
      'Advanced Analytics', 'Business Intelligence', 'Real-Time Data Processing',
      'Data Governance',
    ],
    areasLead: 'We help organisations make faster, better-informed decisions.',
    outcomes: [
      { num: '01', title: 'Decisions you can trust', body: 'Data quality, lineage, and governance that make the dashboard a tool, not a debate.' },
      { num: '02', title: 'Best DB for the workload', body: 'We pick the right engine — SQL, NoSQL, columnar, graph — for the actual question.' },
      { num: '03', title: 'From hindsight to foresight', body: 'Move from rear-view reporting to predictive analytics that change how you operate.' },
    ],
    approachLead: 'Every Data engagement follows the Softfinity consulting approach. Our consultants work directly with your team and stay through the full journey.',
  },
  {
    slug: 'blockchain',
    tag: 'Blockchain',
    pageTitle: 'Blockchain Solutions — Softfinity Consulting',
    title: 'Blockchain Solutions',
    lead: 'Build trust, transparency, and security into digital business processes. Blockchain technology offers opportunities to improve traceability, security, and operational efficiency across industries.',
    heroImage: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Blockchain services',
    areas: [
      'Blockchain Strategy', 'Distributed Ledger Solutions', 'Smart Contracts',
      'Digital Identity Solutions', 'Supply Chain Traceability', 'Asset Tokenisation',
      'Blockchain Integration',
    ],
    areasLead: 'Our experts help organisations evaluate and implement blockchain solutions that deliver practical business value.',
    outcomes: [
      { num: '01', title: "Use it when it earns its place", body: "Honest assessment of whether blockchain is the right tool — and the courage to say no." },
      { num: '02', title: 'Traceability you can prove', body: 'Provenance, audit, and supply-chain integrity that holds up to regulator scrutiny.' },
      { num: '03', title: 'Identity and contracts', body: 'Digital identity and smart contracts that automate real business processes — not whitepapers.' },
    ],
    approachLead: 'Every Blockchain engagement follows the Softfinity consulting approach. Our consultants work directly with your team and stay through the full journey.',
  },
  {
    slug: 'mobile',
    tag: 'Mobile',
    pageTitle: 'Mobile Application Development — Softfinity Consulting',
    title: 'Mobile Application Development',
    lead: 'Build mobile applications people actually use. Customers and employees expect mobile experiences that work properly. Our specialists design and develop applications that help employees do their jobs better and give customers a reason to come back.',
    heroImage: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1600&q=80',
    areasEyebrow: 'Mobile expertise',
    areas: [
      'Native Android Development', 'Native iOS Development', 'Cross-Platform Development',
      'Mobile Security', 'UX/UI Design', 'API Integration',
      'Mobile Modernisation', 'App Maintenance & Support',
    ],
    areasLead: 'We build mobile solutions that do what the business actually needs.',
    outcomes: [
      { num: '01', title: 'Native or cross-platform — your call', body: 'We help you weigh performance, cost, and team skills — and recommend the right path.' },
      { num: '02', title: 'Secure by default', body: 'Mobile security postures built for regulated industries, not bolted on at launch.' },
      { num: '03', title: 'Beyond the launch', body: 'Maintenance and modernisation plans that keep your app relevant over years, not months.' },
    ],
    approachLead: 'Every Mobile engagement follows the Softfinity consulting approach. Our consultants work directly with your team and stay through the full journey.',
  },
];
